"use client";

import Script from "next/script";
import { getAdSessionReadSnippet } from "@/lib/ad-session";

/**
 * UTMAppendScript
 *
 * Intercepts clicks on console.pickcel.com / stage.pickcel.com links and appends
 * stored UTM parameters and click IDs before navigation. This click-time approach:
 * - Avoids modifying hrefs (no conflict with MobileLinkUpdateScript)
 * - No MutationObserver overhead
 * - Works for all links including dynamically added ones
 *
 * Ad-session persistence lives in `lib/ad-session.ts` (localStorage + TTL).
 * This script only reads that flag so console UTM handoff stays gated to paid traffic.
 */
export default function UTMAppendScript() {
  return (
    <Script
      id="utm-append-script"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          (function() {
            var STORAGE_KEY = "pickcel_utm_params";
            var ATTRIBUTION_STORAGE_KEY = "pickcel_attribution_data";
            var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
            var CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"];
            ${getAdSessionReadSnippet()}
            function isAdSession() { return isPickcelAdSession(); }

            function safeParse(value) {
              try { return JSON.parse(value); } catch (_) { return null; }
            }

            function isExpired(expiresAt) {
              if (!expiresAt) return true;
              return new Date(expiresAt).getTime() <= Date.now();
            }

            function getStoredUTMs() {
              var raw = null;
              try { raw = window.localStorage.getItem(STORAGE_KEY); } catch (_) { return {}; }
              if (!raw) return {};
              var persisted = safeParse(raw);
              if (!persisted || !persisted.data || isExpired(persisted.expires_at)) {
                return {};
              }
              var params = {};
              UTM_KEYS.forEach(function(key) {
                if (persisted.data[key]) params[key] = String(persisted.data[key]);
              });
              return params;
            }

            function getStoredClickIds() {
              var raw = null;
              try { raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY); } catch (_) { return {}; }
              if (!raw) return {};
              var persisted = safeParse(raw);
              if (!persisted || !persisted.data || isExpired(persisted.expires_at)) {
                return {};
              }
              var clickIds = persisted.data.click_ids || {};
              var params = {};
              CLICK_ID_KEYS.forEach(function(key) {
                if (clickIds[key]) params[key] = String(clickIds[key]);
              });
              return params;
            }

            function getStoredLandingPage() {
              var raw = null;
              try { raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY); } catch (_) { return null; }
              if (!raw) return null;
              var persisted = safeParse(raw);
              if (!persisted || !persisted.data || isExpired(persisted.expires_at)) {
                return null;
              }
              return persisted.data.landing_page_url || null;
            }

            function stripTrackingParams(rawUrl) {
              if (!rawUrl) return null;
              try {
                var url = new URL(rawUrl);
                UTM_KEYS.forEach(function(key) { url.searchParams.delete(key); });
                return url.toString();
              } catch (_) {
                return rawUrl;
              }
            }

            function getTrackingParams() {
              var utms = getStoredUTMs();
              var clicks = getStoredClickIds();
              var merged = {};
              Object.keys(utms).forEach(function(k) { merged[k] = utms[k]; });
              Object.keys(clicks).forEach(function(k) { merged[k] = clicks[k]; });
              
              // Add landing page URL using Zoho field name (strip tracking params)
              var landingPage = stripTrackingParams(getStoredLandingPage());
              if (landingPage) {
                merged["utm_first_visited_page"] = landingPage;
              }
              
              // Add previous URL (the page where user clicked the button, strip tracking params)
              var lastVisitedPage = stripTrackingParams(window.location.href);
              if (lastVisitedPage) {
                merged["utm_last_visited_page"] = lastVisitedPage;
              }
              
              return merged;
            }

            function pickUtmParams(params) {
              var utmPayload = {};
              Object.keys(params || {}).forEach(function(key) {
                if (key.indexOf("utm_") === 0 && params[key]) {
                  utmPayload[key] = params[key];
                }
              });
              return utmPayload;
            }

            function encodeUtmPayload(payload) {
              try {
                return btoa(JSON.stringify(payload));
              } catch (_) {
                return null;
              }
            }

            function isConsoleLink(href) {
              if (!href || typeof href !== "string") return false;
              var lower = href.toLowerCase();
              if (lower.startsWith("javascript:") || lower.startsWith("#")) return false;
              return lower.indexOf("console.pickcel.com") !== -1 || lower.indexOf("stage.pickcel.com") !== -1;
            }

            function isExcludedPath(href) {
              return href.indexOf("/players/") !== -1 || href.indexOf("/artboard/") !== -1;
            }

            function applyParamsToUrl(href, params) {
              if (!params || Object.keys(params).length === 0) return href;
              try {
                var url = new URL(href, window.location.href);
                var hasHashRoute = url.hash && url.hash.indexOf("#/") === 0;

                if (hasHashRoute) {
                  // Handle hash-based routing (e.g., #/register)
                  var hashContent = url.hash.slice(1); // remove #
                  var hashParts = hashContent.split("?");
                  var hashPath = hashParts[0] || "";
                  var hashQuery = hashParts.slice(1).join("?");
                  var hashParams = new URLSearchParams(hashQuery);

                  Object.keys(params).forEach(function(key) {
                    if (params[key]) hashParams.set(key, params[key]);
                  });

                  var qs = hashParams.toString();
                  url.hash = qs ? "#" + hashPath + "?" + qs : "#" + hashPath;
                } else {
                  // Standard query params
                  Object.keys(params).forEach(function(key) {
                    if (params[key]) url.searchParams.set(key, params[key]);
                  });
                }

                return url.toString();
              } catch (_) {
                return href;
              }
            }

            function resolveUrl(href) {
              try {
                return new URL(href, window.location.href).toString();
              } catch (_) {
                return href;
              }
            }

            function isInternalLink(href) {
              if (!href || typeof href !== "string") return false;
              var lower = href.toLowerCase();
              if (lower.startsWith("javascript:") || lower.startsWith("#") || lower.startsWith("mailto:") || lower.startsWith("tel:")) return false;
              
              // Relative links starting with /
              if (href.startsWith("/") && !href.startsWith("//")) return true;
              
              // Absolute links to the same origin
              try {
                var url = new URL(href, window.location.origin);
                return url.origin === window.location.origin;
              } catch (_) {
                return false;
              }
            }

            function handleClick(event) {
              // Find the anchor element
              var target = event.target;
              while (target && target !== document.body) {
                if (target.tagName && target.tagName.toLowerCase() === "a") break;
                target = target.parentElement;
              }
              if (!target || target.tagName.toLowerCase() !== "a") return;

              // Don't intercept if target="_blank" and it's not a console link
              // (Actually, user said "all", so let's be thorough)
              
              var href = target.getAttribute("href") || target.href || "";
              if (isExcludedPath(href)) return;

              var isConsole = isConsoleLink(href);
              var isInternal = isInternalLink(href);
              
              if (!isConsole && !isInternal) return;

              // Only append if this is a verified ad session (Google Ads UTM or click IDs)
              if (!isAdSession()) return;

              var params = getTrackingParams();
              if (!params || Object.keys(params).length === 0) return;

              var newUrl = href;

              if (isConsole) {
                // Console links use the encoded 'utm' parameter
                var conversionPage = stripTrackingParams(resolveUrl(href));
                if (conversionPage) {
                  params["utm_conversion_page"] = conversionPage;
                }
                var utmPayload = pickUtmParams(params);
                var urlParams = {};
                if (Object.keys(utmPayload).length > 0) {
                  var utmEncoded = encodeUtmPayload(utmPayload);
                  if (utmEncoded) {
                    urlParams.utm = utmEncoded;
                  }
                }
                CLICK_ID_KEYS.forEach(function(key) {
                  if (params[key]) urlParams[key] = params[key];
                });
                if (Object.keys(urlParams).length > 0) {
                  newUrl = applyParamsToUrl(href, urlParams);
                }
              } else if (isInternal) {
                // Internal links use standard UTM parameters
                var utmParamsToApply = {};
                UTM_KEYS.forEach(function(key) {
                  if (params[key]) utmParamsToApply[key] = params[key];
                });
                CLICK_ID_KEYS.forEach(function(key) {
                  if (params[key]) utmParamsToApply[key] = params[key];
                });
                
                if (Object.keys(utmParamsToApply).length > 0) {
                  newUrl = applyParamsToUrl(href, utmParamsToApply);
                }
              }

              if (newUrl && newUrl !== href) {
                // For internal links, we only want to override if the UTMs are NOT already in the URL
                // or if we want to ensure they are always present.
                // However, applyParamsToUrl already handles deduplication.
                
                event.preventDefault();
                event.stopPropagation();
                
                var targetAttr = target.getAttribute("target");
                if (targetAttr === "_blank") {
                  window.open(newUrl, "_blank", target.getAttribute("rel") ? undefined : "noopener,noreferrer");
                } else {
                  window.location.href = newUrl;
                }
              }
            }

            // Single click listener using event delegation
            document.addEventListener("click", handleClick, true);
          })();
        `,
      }}
    />
  );
}
